import { useLazyQuery } from '@apollo/client';
import {
  Criteria,
  GET_IDEA_MEMBERS,
  GET_IDEA_RESULTS_BY_ID,
  Idea,
  Permission,
  User,
} from '@frontend/data-access';
import { UserAvatar } from '@frontend/shared/ui';
import { Progress, Spin } from 'antd';
import { useEffect, useMemo } from 'react';
import { calculateAverageScoreIdeaChannel } from '../idea-evaluate/idea-evaluate';
import { getLinkLabel } from '@frontend/shared/util';
import useTranslation from 'next-translate/useTranslation';

/* eslint-disable-next-line */
export interface IdeaEvaluationResultProps {
  ideaId: string;
}

export function IdeaEvaluationResult({ ideaId }: IdeaEvaluationResultProps) {
  const { t } = useTranslation('common');
  const [fetchIdeaResults, { data, loading }] = useLazyQuery<{
    channelIdea: Idea;
  }>(GET_IDEA_RESULTS_BY_ID, {
    fetchPolicy: 'no-cache',
  });

  const [fetchIdeaMembers, { data: ideaMembersData, loading: membersLoading }] =
    useLazyQuery<{
      getIdeaMembers: {
        user: User;
        permission: Permission;
      }[];
    }>(GET_IDEA_MEMBERS, {
      fetchPolicy: 'no-cache',
    });

  const results = useMemo(
    () => data?.channelIdea.channelCriteriaResults || [],
    [data]
  );
  const ideaMembers = useMemo(
    () => ideaMembersData?.getIdeaMembers || [],
    [ideaMembersData]
  );

  const removedUserResults = useMemo(() => {
    const memberIds = new Set(ideaMembers.map((m) => m.user.id));
    return results.filter((res, index, self) => {
      const isUnique =
        index === self.findIndex((r) => r.createdBy.id === res.createdBy.id);
      return !memberIds.has(res.createdBy.id) && isUnique;
    });
  }, [results, ideaMembers]);

  useEffect(() => {
    fetchIdeaResults({ variables: { ideaId } });
  }, [fetchIdeaResults, ideaId]);

  useEffect(() => {
    if (ideaId) {
      fetchIdeaMembers({ variables: { ideaId } });
    }
  }, [ideaId, fetchIdeaMembers]);

  const getMemberRoleName = (role: string) => {
    if (role === 'CHANNEL_EXPERT') {
      return t('channel_expert');
    } else if (role === 'CHANNEL_REVIEWER') {
      return t('channel_reviewer');
    } else if (role === 'IDEA_REVIEWER') {
      return t('idea_reviewer');
    } else {
      return getLinkLabel(role.toLocaleLowerCase());
    }
  };

  const CriteriaProgressCell = ({ criteria }: { criteria: Criteria }) => {
    const relevantResults = results.filter(
      (r) => r.criteria.id === criteria.id && r.status === 'DONE'
    );
    const averagePercent =
      relevantResults.length > 0
        ? (relevantResults.reduce((sum, r) => sum + r.score, 0) *
            criteria.scale) /
          relevantResults.length
        : undefined;

    return (
      <div className="flex justify-end">
        <div className="text-center space-y-2 font-medium">
          <Progress
            type="circle"
            width={80}
            strokeColor="#595959"
            percent={averagePercent}
            format={(value) => {
              return averagePercent === undefined
                ? '-'
                : (averagePercent / 10).toFixed(1);
            }}
          />
          <div className="text-gray-700 font-normal text-xs">
            {criteria.name}
          </div>
        </div>
      </div>
    );
  };

  const MemberRow = ({
    user,
    role,
    isRemoved,
  }: {
    user: User;
    role: string;
    isRemoved?: boolean;
  }) => {
    return (
      <tr>
        <td className="text-left border-b border-gray-400">
          <div className="flex space-x-2 p-4">
            <UserAvatar user={user} />
            <div>
              <div className="font-semibold">{user.fullName}</div>
              <div className="text-gray-700">
                {user.role !== 'ADMIN' && isRemoved ? (
                  <div className="w-fit text-red-500 bg-red-100 px-2 rounded-sm">
                    {t('removed')}
                  </div>
                ) : (
                  getMemberRoleName(role)
                )}
              </div>
            </div>
          </div>
        </td>
        {data?.channelIdea.channelCriteria?.map((criteria) => {
          const result = results.find(
            (r) => r.criteria.id === criteria.id && r.createdBy.id === user.id
          );
          return (
            <td
              key={criteria.id}
              className="text-right border-b border-gray-400"
            >
              <div className="px-10">
                {result?.score === undefined ? '-' : result?.score}
              </div>
            </td>
          );
        })}
      </tr>
    );
  };

  if (loading || membersLoading) {
    return (
      <div className="p-5 text-center">
        <Spin />
      </div>
    );
  }
  return (
    <div className="overflow-auto">
      <table className="w-full">
        <thead>
          <tr>
            <th className="text-left">
              <div className="px-4">
                <div className="text-gray-700 font-medium">{t('overall')}</div>
                <div className="text-gray-900 font-medium text-4xl">
                  {calculateAverageScoreIdeaChannel(results) || '-'}
                </div>
              </div>
            </th>
            {data?.channelIdea.channelCriteria?.map((criteria) => (
              <th key={criteria.id} className="text-right">
                <CriteriaProgressCell criteria={criteria} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ideaMembers.map((member) => (
            <MemberRow
              key={member.user.id}
              user={member.user}
              role={member.permission.resource.name}
            />
          ))}
          {removedUserResults.map((result) => (
            <MemberRow
              key={result.createdBy.id}
              user={result.createdBy}
              role=""
              isRemoved
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default IdeaEvaluationResult;
